'use client';
import React from 'react';
import {
  ContentManager,
  PodcastPost,
  WordpressPost,
  WordpressSidebar,
  WordpressWidget,
} from '@benzinga/content-manager';
import { SessionContext } from '@benzinga/session-context';
import { useIsUserEditor } from '@benzinga/user-context';
import Hooks from '@benzinga/hooks';
import { Button } from '@benzinga/core-ui';

interface LayoutAdminProps {
  post?: WordpressPost | WordpressSidebar | WordpressWidget | PodcastPost | null;
  showClearCache?: boolean;
  type?: string;
  className?: string;
  wordpressUrl?: string;
  showCloudflareClearCache?: boolean;
  pageUrl?: string;
}

export const LayoutAdmin: React.FC<LayoutAdminProps> = ({
  className,
  pageUrl,
  post,
  showClearCache,
  showCloudflareClearCache = false,
  type,
  wordpressUrl,
}) => {
  const canEdit = Hooks.useHydrate(useIsUserEditor(), false);
  const [isEditorModeEnabled] = Hooks.useLocalStorage('editor_mode_enabled', false);
  const isEditorModeEnabledHydrated = Hooks.useHydrate(isEditorModeEnabled, false);

  const session = React.useContext(SessionContext);

  const handleClick = React.useCallback(() => {
    if (wordpressUrl) {
      window.open(wordpressUrl, '_blank');
    } else {
      post?.id && window.open(`https://www.benzinga.com/wp-admin/post.php?post=${post?.id}&action=edit`, '_blank');
    }
  }, [post?.id, wordpressUrl]);

  const handleClearCache = React.useCallback(() => {
    if (post?.canonical_link) {
      session
        .getManager(ContentManager)
        .clearCache(
          post.canonical_link?.replace('https://www.benzinga.com/', '').replace('https://in.benzinga.com/', ''),
        )
        .then(() => {
          global.location.reload();
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post?.canonical_link]);

  const handleCloudFlareClearCache = React.useCallback(() => {
    if (pageUrl) {
      session
        .getManager(ContentManager)
        .clearCloudFlareCacheByPath(pageUrl)
        .then(() => {
          global.location.reload();
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageUrl]);

  const canShowEdit = !!(post?.id || wordpressUrl);
  const canShowClearCache = !!(showClearCache && post?.canonical_link);
  const canShowCloudflareClearCache = !!(showCloudflareClearCache && pageUrl);

  if (!canShowEdit && !canShowClearCache && !canShowCloudflareClearCache) return null;
  if (!canEdit) return null;
  if (!isEditorModeEnabledHydrated) return null;

  const typeText = (type || post?.type || post?.['template'] || '').replace('_', ' ');

  return (
    <div className={`flex mb-4 ${className}`}>
      {canShowEdit && (
        <Button className="mr-2" onClick={handleClick} variant="primary">
          Edit {typeText}
        </Button>
      )}

      {canShowClearCache && (
        <Button className="mr-2" onClick={handleClearCache} variant="warning">
          Clear cache
        </Button>
      )}
      {canShowCloudflareClearCache && (
        <Button className="mr-2" onClick={handleCloudFlareClearCache} variant="warning">
          Clear Cloudflare cache
        </Button>
      )}
    </div>
  );
};
